import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getTimelineSteps } from '../data/timelineMultilang';
import { useLanguage } from '../contexts/LanguageContext';

const Timeline: React.FC = () => {
  const { language, t } = useLanguage();
  const timelineSteps = getTimelineSteps(language);
  const [activeStep, setActiveStep] = useState<string | null>(null);

  return (
    <section className="py-20 px-6 relative overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Gradient blur backgrounds */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t.timeline.title}
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t.timeline.subtitle}
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {timelineSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                {/* Glass Card */}
                <motion.button
                  onClick={() => {
                    if (activeStep === step.id) {
                      setActiveStep(null);
                    } else {
                      setActiveStep(step.id);
                    }
                  }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full cursor-pointer transition-all duration-500 p-8 ${
                    activeStep === step.id ? 'glass-strong' : 'glass'
                  }`}
                  style={{
                    color: 'var(--color-text-primary)'
                  }}
                >
                  {/* Icon Circle */}
                  <div className="flex justify-center mb-6">
                    <motion.div
                      className="w-24 h-24 rounded-full flex items-center justify-center text-5xl relative z-10"
                      style={{
                        background: activeStep === step.id
                          ? 'linear-gradient(135deg, var(--color-primary), var(--color-accent))'
                          : 'rgba(255, 255, 255, 0.1)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: activeStep === step.id
                          ? '0 8px 32px rgba(99, 102, 241, 0.4)'
                          : '0 4px 16px rgba(0, 0, 0, 0.1)'
                      }}
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      {step.icon}
                    </motion.div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    {step.title}
                  </h3>

                  {/* Subtitle */}
                  <p className="text-sm font-semibold mb-4" style={{
                    color: activeStep === step.id ? 'var(--color-accent)' : 'var(--color-text-secondary)'
                  }}>
                    {step.duration}
                  </p>

                  {/* Description Preview */}
                  <AnimatePresence mode="wait">
                    {activeStep === step.id ? (
                      <motion.div
                        key="expanded"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: 0.15 }}
                          className="text-base leading-relaxed"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {step.description}
                        </motion.p>
                      </motion.div>
                    ) : (
                      <motion.p
                        key="collapsed"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-sm"
                        style={{ color: 'var(--color-text-light)' }}
                      >
                        {t.timeline.clickToLearnMore}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.button>

                {/* Step Number */}
                <div
                  className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg z-20"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
                    color: 'white',
                    boxShadow: '0 4px 16px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  {index + 1}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 flex flex-col sm:flex-row gap-6 justify-center items-center"
        >
          {/* Calculator Button */}
          <motion.a
            href="#calculator"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.98 }}
            className="group relative overflow-hidden px-10 py-5 rounded-2xl glass-strong transition-all duration-300"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-indigo-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            />
            <div className="relative z-10 flex items-center gap-3">
              <span className="text-3xl">💰</span>
              <div>
                <div className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  {t.timeline.calculateROI}
                </div>
                <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.timeline.seeYourSavings}
                </div>
              </div>
            </div>
          </motion.a>

          {/* Get Started Button */}
          <motion.a
            href="#get-started"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.98 }}
            className="relative overflow-hidden px-10 py-5 rounded-2xl transition-all duration-300 group"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)'
            }}
          >
            <motion.div
              className="absolute inset-0 bg-white/20"
              animate={{
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }}
            />
            <div className="relative z-10 flex items-center gap-3">
              <span className="text-3xl">🚀</span>
              <div>
                <div className="text-xl font-bold text-white">
                  {t.timeline.getFreePOC}
                </div>
                <div className="text-sm text-white/90">
                  {t.timeline.noCommitment}
                </div>
              </div>
            </div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Timeline;
